import { gql } from "@apollo/client";

export const GET_PROFESSIONAL_SERVICES = gql`
    query GetProfessionalServices($pageIndex: Int!, $pageSize: Int!, $orderAsc: Boolean!, $orderField: String!, $filters: ProfessionalServiceFilterInput) {
        professionalServices(pagedInput: {
            pageInfo: {
                pageIndex: $pageIndex,
                pageSize: $pageSize
            },
            orderAsc: $orderAsc,
            orderField: $orderField
        }, filters: $filters) {
            items {
                id
                companyName
                tradingName
                type
                specializations
                serviceAreas
                primaryContact {
                    firstName
                    lastName
                    title
                    email
                    cellphone
                    telephone
                }
                alternativeContact {
                    firstName
                    lastName
                    email
                    cellphone
                }
                performance {
                    clientSatisfaction
                    averageResponseTime
                    completionRate
                    dealsCompleted
                    lastPerformanceUpdate
                }
                active
                registrationNumber
                vatNumber
                licenseNumber
                professionalBody
                createdAt
                updatedAt
            }
            pageInfo {
                totalCount
                totalPages
                pageSize
                pageIndex
                hasNextPage
                hasPreviousPage
            }
        }
    }
`;

export const GET_PROFESSIONAL_SERVICE = gql`
    query GetProfessionalService($id: ID!) {
        professionalService(id: $id) {
            id
            companyName
            tradingName
            type
            specializations
            serviceAreas
            primaryContact {
                firstName
                lastName
                title
                email
                cellphone
                telephone
                fax
            }
            alternativeContact {
                firstName
                lastName
                title
                email
                cellphone
                telephone
                fax
            }
            physicalAddress {
                number
                street
                suburb
                city
                province
                postalCode
                complexName
                unitNumber
            }
            postalAddress {
                number
                street
                suburb
                city
                province
                postalCode
                complexName
                unitNumber
            }
            website
            bankingDetails {
                bankName
                accountNumber
                accountType
                branchCode
                accountHolder
            }
            registrationNumber
            vatNumber
            licenseNumber
            professionalBody
            performance {
                clientSatisfaction
                averageResponseTime
                completionRate
                dealsCompleted
                lastPerformanceUpdate
            }
            active
            notes
            createdAt
            updatedAt
            createdBy {
                id
                firstName
                lastName
            }
            updatedBy {
                id
                firstName
                lastName
            }
        }
    }
`;

export const GET_SERVICES_FOR_DROPDOWN = gql`
    query GetServicesForDropdown($type: ProfessionalServiceType, $specialization: String) {
        professionalServicesForDropdown(type: $type, specialization: $specialization) {
            id
            companyName
            tradingName
            type
            primaryContact {
                firstName
                lastName
                email
                cellphone
            }
            performance {
                clientSatisfaction
                averageResponseTime
            }
        }
    }
`;

export const GET_SERVICES_BY_TYPE = gql`
    query GetServicesByType($type: ProfessionalServiceType!, $activeOnly: Boolean) {
        professionalServicesByType(type: $type, activeOnly: $activeOnly) {
            id
            companyName
            tradingName
            specializations
            primaryContact {
                firstName
                lastName
                email
                cellphone
            }
            performance {
                clientSatisfaction
                completionRate
                dealsCompleted
            }
            active
        }
    }
`;

export const GET_SERVICE_STATISTICS = gql`
    query GetServiceStatistics {
        professionalServiceStatistics {
            typeDistribution {
                type
                count
                averageRating
                averageResponseTime
            }
            specializationDistribution {
                specialization
                count
                types
            }
            topPerformers {
                id
                companyName
                type
                performance {
                    clientSatisfaction
                    completionRate
                    dealsCompleted
                }
            }
            overallStats {
                totalServices
                averageRating
                averageResponseTime
                averageCompletionRate
                totalDealsCompleted
            }
        }
    }
`;

export const GET_RECOMMENDED_SERVICES = gql`
    query GetRecommendedServices($type: ProfessionalServiceType!, $serviceArea: String, $minRating: Float, $limit: Int) {
        recommendedServices(type: $type, serviceArea: $serviceArea, minRating: $minRating, limit: $limit) {
            id
            companyName
            tradingName
            specializations
            serviceAreas
            primaryContact {
                firstName
                lastName
                email
                cellphone
            }
            performance {
                clientSatisfaction
                averageResponseTime
                completionRate
                dealsCompleted
            }
        }
    }
`;

export const SEARCH_SERVICES_BY_NAME = gql`
    query SearchServicesByName($searchTerm: String!, $type: ProfessionalServiceType, $activeOnly: Boolean) {
        searchProfessionalServicesByName(searchTerm: $searchTerm, type: $type, activeOnly: $activeOnly) {
            id
            companyName
            tradingName
            type
            primaryContact {
                firstName
                lastName
                email
                cellphone
            }
            performance {
                clientSatisfaction
                completionRate
            }
            active
        }
    }
`;

export const CREATE_PROFESSIONAL_SERVICE = gql`
    mutation CreateProfessionalService($inputData: ProfessionalServiceInput!) {
        createProfessionalService(inputData: $inputData) {
            id
            companyName
            tradingName
            type
            specializations
            serviceAreas
            primaryContact {
                firstName
                lastName
                email
                cellphone
            }
            active
            createdAt
        }
    }
`;

export const UPDATE_PROFESSIONAL_SERVICE = gql`
    mutation UpdateProfessionalService($id: ID!, $inputData: ProfessionalServiceInput!) {
        updateProfessionalService(id: $id, inputData: $inputData) {
            id
            companyName
            tradingName
            type
            specializations
            serviceAreas
            primaryContact {
                firstName
                lastName
                email
                cellphone
            }
            active
            updatedAt
        }
    }
`;

export const DELETE_PROFESSIONAL_SERVICE = gql`
    mutation DeleteProfessionalService($id: ID!) {
        deleteProfessionalService(id: $id)
    }
`;

export const DEACTIVATE_PROFESSIONAL_SERVICE = gql`
    mutation DeactivateProfessionalService($id: ID!, $reason: String) {
        deactivateProfessionalService(id: $id, reason: $reason) {
            id
            active
            notes
            updatedAt
        }
    }
`;

export const REACTIVATE_PROFESSIONAL_SERVICE = gql`
    mutation ReactivateProfessionalService($id: ID!) {
        reactivateProfessionalService(id: $id) {
            id
            active
            updatedAt
        }
    }
`;

export const UPDATE_PERFORMANCE_METRICS = gql`
    mutation UpdatePerformanceMetrics($id: ID!, $performanceData: PerformanceMetricsInput!) {
        updatePerformanceMetrics(id: $id, performanceData: $performanceData) {
            id
            performance {
                clientSatisfaction
                averageResponseTime
                completionRate
                dealsCompleted
                lastPerformanceUpdate
            }
            updatedAt
        }
    }
`;

export const INCREMENT_DEALS_COMPLETED = gql`
    mutation IncrementDealsCompleted($id: ID!) {
        incrementDealsCompleted(id: $id) {
            id
            performance {
                dealsCompleted
                lastPerformanceUpdate
            }
            updatedAt
        }
    }
`;

export const CALCULATE_PERFORMANCE_SCORE = gql`
    mutation CalculatePerformanceScore($id: ID!) {
        calculatePerformanceScore(id: $id)
    }
`;

export const BULK_UPDATE_ACTIVE_STATUS = gql`
    mutation BulkUpdateServiceActiveStatus($serviceIds: [ID!]!, $active: Boolean!) {
        bulkUpdateServiceActiveStatus(serviceIds: $serviceIds, active: $active)
    }
`;